import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { useChat } from '@/contexts/ChatContext'
import { useUnreadMessages } from './useUnreadMessages'

export function useInboxSoundNotification() {
  const location = useLocation()
  const chatContext = useChat()
  const { totalUnreadCount } = useUnreadMessages()
  const previousUnreadCountRef = useRef<number>(0)
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const lastPlayTimeRef = useRef<number>(0)
  const SOUND_COOLDOWN = 1000 // Prevent playing sound more than once per second

  const playNotificationSound = () => {
    const now = Date.now()

    if (now - lastPlayTimeRef.current < SOUND_COOLDOWN) {
      return
    }

    lastPlayTimeRef.current = now

    const audioFile = new Audio('/assets/notification-sound.mp3')
    audioFile.volume = 0.5 // Set volume to 50%

    audioFile.play().catch((error) => {

      try {
        const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)()
        const oscillator = audioContext.createOscillator()
        const gainNode = audioContext.createGain()

        oscillator.connect(gainNode)
        gainNode.connect(audioContext.destination)

        oscillator.frequency.setValueAtTime(800, audioContext.currentTime)
        oscillator.frequency.setValueAtTime(1000, audioContext.currentTime + 0.1)
        oscillator.type = 'sine'

        gainNode.gain.setValueAtTime(0.3, audioContext.currentTime)
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.2)

        oscillator.start(audioContext.currentTime)
        oscillator.stop(audioContext.currentTime + 0.2)
      } catch (webAudioError) {
      }
    })
  }

  useEffect(() => {
    if (!chatContext) {
      return
    }

    const { socket, isConnected } = chatContext
    if (!socket || !isConnected) return

    const isInInbox = location.pathname === '/home/inbox' || location.pathname.startsWith('/home/inbox/')

    const handleNewMessage = (data: any) => {
      const payload = data?.message ?? data
      const isInbound = payload.isInbound || payload.is_inbound ||
        ((payload.platform === 'facebook' || payload.platform === 'instagram') && !payload.isFromBusiness)

      if (isInbound && !isInInbox && document.visibilityState === 'visible') {
        playNotificationSound()
      }
    }

    const handleConversationUpdate = (data: any) => {
      if (
        data.type === 'new_message' &&
        (data.isInbound || data.is_inbound) &&
        !isInInbox &&
        document.visibilityState === 'visible'
      ) {
        playNotificationSound()
      }
    }

    socket.on('new-message', handleNewMessage)
    socket.on('conversation_updated', handleConversationUpdate)

    return () => {
      socket.off('new-message', handleNewMessage)
      socket.off('conversation_updated', handleConversationUpdate)
    }
  }, [chatContext, location.pathname])

  useEffect(() => {
    const isInInbox = location.pathname === '/home/inbox' || location.pathname.startsWith('/home/inbox/')

    if (
      totalUnreadCount > previousUnreadCountRef.current &&
      !isInInbox &&
      document.visibilityState === 'visible'
    ) {
      playNotificationSound()
    }

    previousUnreadCountRef.current = totalUnreadCount
  }, [totalUnreadCount, location.pathname])
}

